import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Headphones, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dj/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/dj/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="neon-grid min-h-screen flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl beam mb-4">
            <Headphones className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-3xl font-black text-white text-glow">
            Console DJ
          </h1>
          <p className="mt-2 text-slate-400 text-sm font-mono uppercase tracking-[0.2em]">
            Accesso riservato
          </p>
        </div>

        <form
          onSubmit={submit}
          className="neon-card rounded-2xl p-6 sm:p-8 space-y-5"
        >
          <div>
            <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
              Email
            </label>
            <input
              data-testid="dj-login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dj@serata.it"
              className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-4 py-3 text-white placeholder:text-slate-600 transition-colors"
            />
          </div>
          <div>
            <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
              Password
            </label>
            <input
              data-testid="dj-login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-4 py-3 text-white placeholder:text-slate-600 transition-colors"
            />
          </div>

          {error && (
            <p
              data-testid="dj-login-error"
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="dj-login-submit-button"
            disabled={loading}
            className="w-full beam text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Entra in console
              </>
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-slate-600 text-xs font-mono">
          Solo per il DJ della serata
        </p>
      </motion.div>
    </div>
  );
}
